import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiArrowsClockwiseBold,
    PiCalendarBlankBold,
    PiToggleLeftBold,
    PiToggleRightFill,
} from 'react-icons/pi';
import { Button, PageHeader } from '@v2/design-system';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';

type CommissionSettingItem = { key: string; value: string; description: string | null };

type PreNfSyncStats = {
    nf_start_date: string;
    dry_run: boolean;
    deals_touched: number;
    installments_marked_paid: number;
    records_created_or_updated: number;
    deal_records_adjusted: number;
    installments_reopened: number;
    records_reverted: number;
};

function NfSellerEmailsSettingsCard() {
    const qc = useQueryClient();

    const { data, isLoading } = useQuery<{ data: CommissionSettingItem[] }>({
        queryKey: ['commissioning', 'settings'],
        queryFn: () => api.get('/commissioning/settings').then((r) => r.data),
    });

    const item = data?.data?.find((s) => s.key === 'nf_seller_emails_enabled');
    const isActive = item ? item.value !== '0' && item.value !== 'false' : true;

    const toggleMutation = useMutation({
        mutationFn: (active: boolean) =>
            api.put('/commissioning/settings', {
                settings: [{
                    key: 'nf_seller_emails_enabled',
                    value: active ? '1' : '0',
                    description: 'Habilita envio de e-mails de NF para vendedores',
                }],
            }),
        onSuccess: (_, active) => {
            toast.success(active ? 'E-mails de NF para vendedores ativados.' : 'E-mails de NF para vendedores desativados.');
            qc.invalidateQueries({ queryKey: ['commissioning', 'settings'] });
        },
        onError: () => toast.error('Erro ao alterar status dos e-mails de NF.'),
    });

    return (
        <div className={`rounded-xl border p-4 bg-white flex items-center gap-4 ${!isActive ? 'opacity-70 border-amber-300' : 'border-gray-200'}`}>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                        Notificações NF
                    </span>
                    <span className="font-medium text-gray-900">Envio de e-mails de NF para vendedores</span>
                    {!isActive && <span className="text-xs text-amber-700">(desativado)</span>}
                </div>
                <p className="text-xs text-gray-500">
                    {isLoading
                        ? 'Carregando configuração...'
                        : isActive
                            ? 'Ativo: o sistema envia e-mails de solicitação/lembrete/confirmação de NF para vendedores.'
                            : 'Desativado: o sistema não envia e-mails de NF para vendedores (inclui solicitação, lembretes e confirmação).'}
                </p>
            </div>
            <button
                title={isActive ? 'Desativar e-mails de NF para vendedores' : 'Ativar e-mails de NF para vendedores'}
                disabled={toggleMutation.isPending}
                onClick={() => toggleMutation.mutate(!isActive)}
                className={`p-2 rounded-lg transition-colors ${isActive ? 'text-emerald-600 hover:bg-emerald-50' : 'text-gray-400 hover:bg-gray-100'}`}
            >
                {isActive
                    ? <PiToggleRightFill className="w-5 h-5" />
                    : <PiToggleLeftBold className="w-5 h-5" />}
            </button>
        </div>
    );
}

function NfStartDateSettingsCard() {
    const { t } = useI18n();
    const qc = useQueryClient();
    const [date, setDate] = useState('2026-08-01');

    const { data, isLoading } = useQuery<{ data: CommissionSettingItem[] }>({
        queryKey: ['commissioning', 'settings'],
        queryFn: () => api.get('/commissioning/settings').then((r) => r.data),
    });

    useEffect(() => {
        const item = data?.data?.find((s) => s.key === 'nf_start_date');
        if (item?.value) {
            setDate(item.value.slice(0, 10));
        }
    }, [data]);

    const saveMutation = useMutation({
        mutationFn: (value: string) =>
            api.put('/commissioning/settings', {
                sync_pre_nf: true,
                settings: [{
                    key: 'nf_start_date',
                    value,
                    description: 'Data de início do fluxo de NF no Hub (parcelas anteriores = pré-NF / paid_at = due_date)',
                }],
            }),
        onSuccess: (res) => {
            const sync = res.data?.pre_nf_sync as PreNfSyncStats | undefined;
            if (sync) {
                toast.success(t('v2.commissioning.nf_start.save_success', {
                    deals: sync.deals_touched,
                    installments: sync.installments_marked_paid,
                    records: sync.records_created_or_updated,
                }));
            } else {
                toast.success(t('v2.commissioning.nf_start.save_success_simple'));
            }
            qc.invalidateQueries({ queryKey: ['commissioning', 'settings'] });
            qc.invalidateQueries({ queryKey: ['my-seller-profile'] });
            qc.invalidateQueries({ queryKey: ['seller-profile'] });
        },
        onError: () => toast.error(t('v2.commissioning.nf_start.error')),
    });

    const syncMutation = useMutation({
        mutationFn: (dryRun: boolean) =>
            api.post('/commissioning/settings/sync-pre-nf', { dry_run: dryRun }),
        onSuccess: (res) => {
            const sync = res.data?.data as PreNfSyncStats;
            toast.success(t('v2.commissioning.nf_start.sync_success', {
                deals: sync.deals_touched,
                installments: sync.installments_marked_paid,
                records: sync.records_created_or_updated,
            }));
            if (!sync.dry_run) {
                qc.invalidateQueries({ queryKey: ['my-seller-profile'] });
                qc.invalidateQueries({ queryKey: ['seller-profile'] });
            }
        },
        onError: () => toast.error(t('v2.commissioning.nf_start.error')),
    });

    const busy = saveMutation.isPending || syncMutation.isPending;

    return (
        <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 dark:border-amber-500/20 dark:bg-amber-500/5">
            <div className="mb-3 flex items-start gap-3">
                <div className="rounded-lg bg-amber-100 p-2 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">
                    <PiCalendarBlankBold className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">
                            {t('v2.commissioning.nf_start.badge')}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-zinc-100">
                            {t('v2.commissioning.nf_start.title')}
                        </span>
                    </div>
                    <p className="text-xs leading-relaxed text-gray-600 dark:text-zinc-400">
                        {isLoading ? '…' : t('v2.commissioning.nf_start.help')}
                    </p>
                </div>
            </div>

            <div className="flex flex-wrap items-end gap-3">
                <label className="block min-w-[200px] flex-1">
                    <span className="mb-1 block text-[10px] font-black uppercase tracking-widest text-gray-500">
                        {t('v2.commissioning.nf_start.label')}
                    </span>
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/30 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
                    />
                </label>
                <Button
                    size="sm"
                    disabled={busy || !date}
                    onClick={() => saveMutation.mutate(date)}
                >
                    {saveMutation.isPending
                        ? t('v2.commissioning.nf_start.saving')
                        : t('v2.commissioning.nf_start.save')}
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => syncMutation.mutate(false)}
                >
                    <PiArrowsClockwiseBold className={`me-1 h-3.5 w-3.5 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                    {t('v2.commissioning.nf_start.sync_btn')}
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => syncMutation.mutate(true)}
                >
                    {t('v2.commissioning.nf_start.sync_dry')}
                </Button>
            </div>
        </div>
    );
}

export default function CommissionSettingsPage() {
    return (
        <div className="space-y-6">
            <PageHeader
                title="Configurações Gerais do Comissionamento"
                subtitle="Parâmetros globais de NF e notificações"
            />

            <NfStartDateSettingsCard />
            <NfSellerEmailsSettingsCard />
        </div>
    );
}
